import React, { useState, useEffect } from "react";
import Cookies from "js-cookie";
import { Pagination, Row, Col, Button, Space, Modal } from "antd";
import {
  FilterOutlined,
  MenuOutlined,
  AppstoreOutlined,
  EnvironmentOutlined,
  PlusOutlined,
  MinusOutlined,
  CloseOutlined,
  HeartOutlined,
} from "@ant-design/icons";
import "./searchresults.css";
import HotelObject from "../HotelObject/hotelobject";
import { HotelsProvider } from "../HotelObject/hotelobject";
import HotelsFilters from "./HotelFilters/hotelfilters";
import PackageFilter from "./PackageFilter/PackageFilter";
import { useTranslation } from "react-i18next";

import {
  HOTELS,
  ORGANIZED_TOURS,
  PACKAGES,
  DYNAMIC_PACKAGES,
  useSearch,
} from "../../../../contexts/SearchContext";
import PackageObject from "../PackageObject/packageobject";
import { Loader } from "../../../Loader/Loader";
import Timer from "../../../../Pages/Timer/Timer";
import TourObject from "../OrganizedTourObject/tourObject";
import OrganizedTourFilter from "./OrganizedTourFilter";
import FlightSelection from "../../../FlightSelection/Index";
import HotelMapView from "../HotelMapView";
import MobileMapToggle from "../MobileMapToggle";
import MobileHotelPopup from "../MobileHotelPopup";
import { GoogleMapSecretKey } from "../../../../assets/GlobalVariables";
import { useAppContext } from "../../../../contexts/AppContext";

// View mode enum
const VIEW_MODES = {
  CARDS: "cards",
  LIST: "list",
  MAP: "map",
  LIKED: "liked",
};

const SearchResults = () => {
  const { isMobile } = useAppContext();
  const {
    searchPhrase,
    linkedFlights,
    searchResults,
    sessionId,
    searchTab,
    loading,
    rangeSearchResults,
    location,
  } = useSearch();

  const [filters, setFilters] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const { t } = useTranslation();

  const initialPageIndex = 0;
  const [viewMode, setViewMode] = useState(VIEW_MODES.CARDS);
  const [pageIndex, setPageIndex] = useState(initialPageIndex);
  const [searchResultsFiltered, setSearchResultsFiltered] =
    useState(searchResults);
  const [totalResults, setTotalResults] = useState(0);
  const [initialLoadedItems, setInitialLoadedItems] = useState(15);
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [isMapVisible, setIsMapVisible] = useState(false); // For mobile toggle
  const [showMobilePopup, setShowMobilePopup] = useState(false); // For mobile hotel popup
  const [isHovered, setIsHoveredId] = useState(0);
  const [savedHotels, setSavedHotels] = useState([]);
  const [isLikedClicked, setIsLikedClicked] = useState(false);
  const [isFiltering, setIsFiltering] = useState(false);
  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const resetToOriginalResults = () => {
    if (isLikedClicked) {
      setSearchResultsFiltered(searchResults);
      setIsLikedClicked(false);
      setTotalResults(searchResults?.length || 0);
      setPageIndex(0); // Reset pagination
    }
  };

  // Handle window resize and set mobile state
  useEffect(() => {
    if (isMobile && viewMode === VIEW_MODES.MAP) {
      setViewMode(VIEW_MODES.CARDS);
    }
  }, [isMobile, viewMode]);

  const handleMouseOver = (id) => {
    setIsHoveredId(id);
  };

  const fetchLikedHotels = () => {
    if (isLikedClicked) {
      resetToOriginalResults();
      return;
    }

    // Check if both arrays have content
    if (savedHotels.length === 0) {
      alert("No saved hotels found");
      return; // Early return if no saved hotels
    }

    if (searchResultsFiltered.length === 0) {
      console.log("No search results to filter");
      return; // Early return if no search results
    }

    // If types don't match, convert savedHotels IDs to the same type as hotel IDs
    let normalizedSavedHotels = savedHotels;
    if (typeof searchResultsFiltered[0].ID !== typeof savedHotels[0]) {
      console.log("Type mismatch detected, normalizing types");
      if (typeof searchResultsFiltered[0].ID === "number") {
        normalizedSavedHotels = savedHotels.map((id) => Number(id));
      } else if (typeof searchResultsFiltered[0].ID === "string") {
        normalizedSavedHotels = savedHotels.map((id) => String(id));
      }
    }

    // Filter with normalized IDs
    const newSearchResults = searchResultsFiltered.filter((hotel) =>
      normalizedSavedHotels.includes(hotel.ID)
    );

    // Only update state if we have results
    if (newSearchResults.length > 0) {
      setSearchResultsFiltered(newSearchResults);
      setIsLikedClicked(true);
      setTotalResults(newSearchResults.length);
      setViewMode(VIEW_MODES.LIKED);
    } else {
      alert(
        "או שלא שמרת מלונות במועדפים , או שהמלונות ששמרת לא נמצאים תחת החיפוש הזה"
      );
    }
  };

  const handleMouseOut = () => {
    setIsHoveredId(0);
  };

  // Scroll to top when changing pages
  useEffect(() => {
    const page = document.querySelector(".agent-component");
    if (page) {
      // smooth scroll to top
      page.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [pageIndex]);

  // Update search results when they change
  useEffect(() => {
    setSearchResultsFiltered(searchResults);
    setPageIndex(0);
    setTotalResults(searchResults?.length || 0);
    setSelectedHotel(null); // Reset selected hotel when results change
  }, [searchResults]);

  // Function to handle hotel selection from map
  function handleHotelSelect(hotel) {
    setSelectedHotel(hotel);
  }
  // Toggle map view for mobile
  function toggleMapView() {
    setIsMapVisible(!isMapVisible);
  }

  function handleViewDetails() {
    setShowMobilePopup(false);
    setIsMapVisible(false);

    // Allow time for the UI to update before scrolling
    setTimeout(() => {
      const hotelElement = document.getElementById(selectedHotel?.ID);
      if (hotelElement) {
        hotelElement.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 100);
  }

  const likeHotel = (id) => {
    // Make a copy of the current savedHotels array
    let updatedHotels = [...savedHotels];

    if (updatedHotels.includes(id)) {
      // Remove the ID
      updatedHotels = updatedHotels.filter((hotelId) => hotelId !== id);
    } else {
      // Add the ID
      updatedHotels.push(id);
    }

    // Update state
    setSavedHotels(updatedHotels);

    // Save the updated array back to the cookie
    Cookies.set("savedHotels", JSON.stringify(updatedHotels), { expires: 7 });
  };

  const setIsFilteringFunc = (value) => {
    setIsFiltering(value);
  };

  useEffect(() => {
    const savedHotelsCookie = Cookies.get("savedHotels");
    if (savedHotelsCookie) {
      try {
        const parsedHotels = JSON.parse(savedHotelsCookie);
        setSavedHotels(parsedHotels);
      } catch (error) {
        console.error("Error parsing saved hotels from cookie:", error);
        setSavedHotels([]);
      }
    }
  }, []);

  function renderResults() {
    if (loading || isFiltering) {
      return (
        <div
          className="loader-container"
          style={{
            width: "100vw",
            height: "100vh",
            position: "fixed", // Changed from absolute to fixed
            top: 0,
            left: 0,
          }}
        >
          <Loader showText />
        </div>
      );
    }

    if (!searchResults?.length) {
      return null;
    }

    if (searchResultsFiltered?.length === 0) {
      return (
        <div className="tw-mb-4 tw-text-center tw-text-gray-500 tw-w-full tw-h-full tw-min-h-[70vh]">
          אין תוצאות לחיפוש
        </div>
      );
    }

    // On desktop: show map view if viewMode is MAP
    // On mobile: show map only if isMapVisible is true (toggled)
    const showMap =
      (!isMobile && viewMode === VIEW_MODES.MAP) || (isMobile && isMapVisible);

    if (
      (showMap && searchTab === HOTELS) ||
      (showMap && searchTab === DYNAMIC_PACKAGES)
    ) {
      return (
        <HotelsProvider
          hotels={searchResultsFiltered?.slice(
            pageIndex * initialLoadedItems,
            initialLoadedItems * (pageIndex + 1)
          )}
        >
          <div className="tw-flex tw-flex-col lg:tw-flex-row tw-w-full tw-h-[70vh] xl:tw-h-[75vh] md:tw-p-2 tw-rounded-lg ">
            {!isMobile && (
              <div className="tw-w-full lg:tw-w-1/2 tw-h-full tw-overflow-hidden tw-z-0">
                {/* <div className="tw-p-2 tw-pt-0 lg:tw-p-4 lg:tw-pt-0 tw-h-full tw-overflow-y-auto tw-scrollbar-hidden"> */}
                <div
                  className={`tw-p-2 tw-pt-0 lg:tw-p-4 lg:tw-pt-2 tw-gap-2  tw-flex tw-flex-col tw-overflow-y-scroll tw-scrollbar-hidden  tw-h-[90%] tw-z-0`}
                >
                  {searchResultsFiltered
                    ?.slice(
                      pageIndex * initialLoadedItems,
                      initialLoadedItems * (pageIndex + 1)
                    )
                    ?.map((searchItem, i) => {
                      return (
                        <div
                          className="div"
                          onMouseOver={() => handleMouseOver(searchItem.ID)}
                          onMouseOut={handleMouseOut}
                          key={i}
                        >
                          {searchTab === HOTELS ? (
                            <HotelObject
                              key={HOTELS + "-" + i}
                              hotelsItem={searchItem}
                              searchPhrase={searchPhrase}
                              searchSession={sessionId}
                              isHighlighted={
                                selectedHotel?.ID === searchItem.ID
                              }
                              likeHotel={likeHotel}
                              savedHotels={savedHotels}
                            />
                          ) : searchTab === DYNAMIC_PACKAGES ? (
                            <HotelObject
                              key={DYNAMIC_PACKAGES + "-" + i}
                              hotelsItem={searchItem}
                              searchSession={sessionId}
                              likeHotel={likeHotel}
                              savedHotels={savedHotels}
                            />
                          ) : searchTab === PACKAGES ? (
                            <PackageObject
                              key={PACKAGES + "-" + i}
                              {...searchItem}
                            />
                          ) : searchTab === ORGANIZED_TOURS ? (
                            <TourObject
                              key={ORGANIZED_TOURS + "-" + i}
                              {...searchItem}
                            />
                          ) : null}
                        </div>
                      );
                    })}
                </div>
                <div className="tw-w-full tw-flex tw-justify-center tw-pb-5 tw-h-[4%]">
                  {totalResults > 0 ? (
                    <Pagination
                      defaultCurrent={1}
                      total={totalResults}
                      pageSize={initialLoadedItems}
                      showSizeChanger={false}
                      onChange={(page, pageSize) => {
                        setPageIndex(page - 1);
                        setInitialLoadedItems(pageSize);
                      }}
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        width: "100%",
                        paddingBottom: "10px",
                        minHeight: "50px",
                      }}
                    />
                  ) : null}
                </div>
              </div>
            )}
            {/* Map section - takes 50% width on desktop, full width on mobile */}
            <div className="tw-w-full md:tw-w-1/2 tw-h-full tw-bg-white tw-rounded-lg md:tw-mx-4 tw-flex tw-justify-end ">
              <HotelMapView
                searchResults={searchResultsFiltered}
                onHotelSelect={handleHotelSelect}
                isMobile={isMobile}
                location={location}
                hoveredId={isHovered}
              />
            </div>
          </div>
        </HotelsProvider>
      );
    }

    // When map is not enabled, show only results (full width)
    return (
      <HotelsProvider
        hotels={searchResultsFiltered?.slice(
          pageIndex * initialLoadedItems,
          initialLoadedItems * (pageIndex + 1)
        )}
      >
        <div
          className={`${searchResultsFiltered?.length > 0 ? " rounded-lg " : ""} tw-w-full tw-h-full`}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns:
                searchTab === HOTELS || searchTab === DYNAMIC_PACKAGES
                  ? viewMode === VIEW_MODES.CARDS ||
                    viewMode === VIEW_MODES.LIKED
                    ? "repeat(auto-fill, minmax(260px, 1fr))"
                    : "repeat(auto-fill, minmax(500px, 1fr))"
                  : "repeat(auto-fill, minmax(360px, 1fr))",
              gridAutoRows: "min-content",
              gridAutoFlow: "dense",
              width: "100%",
              height: "100%",
              gap: "10px",
            }}
          >
            {searchResultsFiltered
              ?.slice(
                pageIndex * initialLoadedItems,
                initialLoadedItems * (pageIndex + 1)
              )
              ?.map((searchItem, i) => {
                return (
                  <div
                    key={i}
                    className={`tw-h-full tw-flex tw-flex-col tw-w-full ${viewMode === VIEW_MODES.CARDS || viewMode === VIEW_MODES.LIKED ? "md:tw-col-span-1" : ""}`}
                  >
                    <div className="tw-flex-1 tw-flex tw-flex-col tw-h-full md:tw-max-h-full">
                      {searchTab === HOTELS ? (
                        <HotelObject
                          key={HOTELS + "-" + i}
                          hotelsItem={searchItem}
                          searchPhrase={searchPhrase}
                          searchSession={sessionId}
                          isHighlighted={selectedHotel?.ID === searchItem.ID}
                          likeHotel={likeHotel}
                          savedHotels={savedHotels}
                        />
                      ) : searchTab === DYNAMIC_PACKAGES ? (
                        <HotelObject
                          key={DYNAMIC_PACKAGES + "-" + i}
                          hotelsItem={searchItem}
                          searchSession={sessionId}
                          likeHotel={likeHotel}
                          savedHotels={savedHotels}
                        />
                      ) : searchTab === PACKAGES ? (
                        <PackageObject
                          key={PACKAGES + "-" + i}
                          {...searchItem}
                        />
                      ) : searchTab === ORGANIZED_TOURS ? (
                        <TourObject
                          key={ORGANIZED_TOURS + "-" + i}
                          {...searchItem}
                        />
                      ) : null}
                    </div>
                  </div>
                );
              })}
          </div>
          {totalResults > 0 ? (
            <Pagination
              defaultCurrent={1}
              total={totalResults}
              pageSize={initialLoadedItems}
              showSizeChanger={false}
              onChange={(page, pageSize) => {
                setPageIndex(page - 1);
                setInitialLoadedItems(pageSize);
              }}
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                paddingBottom: "10px",
                paddingTop: "10px",
                minHeight: "50px",
                ...(!searchResultsFiltered?.length > 0 && { display: "none" }),
                width: "100%",
              }}
            />
          ) : null}
        </div>
      </HotelsProvider>
    );
  }

  // Render filter components based on search tab
  function renderFilters() {
    const showMap =
      (!isMobile && viewMode === VIEW_MODES.MAP) || (isMobile && isMapVisible);

    switch (searchTab) {
      case HOTELS:
      case DYNAMIC_PACKAGES:
        return (
          <HotelsFilters
            setSearchResultsFiltered={setSearchResultsFiltered}
            searchResultsFiltered={searchResultsFiltered}
            searchResults={searchResults}
            filters={filters}
            showMap={showMap}
            onFiltering={setIsFilteringFunc}
          />
        );

      case PACKAGES:
        return (
          <PackageFilter
            setSearchResultsFiltered={setSearchResultsFiltered}
            searchResultsFiltered={searchResultsFiltered}
            searchResults={searchResults}
          />
        );

      case ORGANIZED_TOURS:
        return (
          <OrganizedTourFilter
            setSearchResultsFiltered={setSearchResultsFiltered}
            searchResultsFiltered={searchResultsFiltered}
            searchResults={searchResults}
          />
        );

      default:
        return null;
    }
  }

  // Desktop view mode buttons
  const ViewModeButtons = () => (
    <Space.Compact className="tw-h-[50px]">
      <Button
        type={viewMode === VIEW_MODES.CARDS ? "default" : "text"}
        onClick={() => {
          setViewMode(VIEW_MODES.CARDS);
          resetToOriginalResults(); // Reset when changing views
        }}
        icon={<AppstoreOutlined />}
        style={{
          backgroundColor: viewMode === VIEW_MODES.CARDS ? "#e0e0e0" : "",
          height: 50,
          width: 50,
        }}
      />

      <Button
        type={viewMode === VIEW_MODES.LIST ? "default" : "text"}
        onClick={() => {
          setViewMode(VIEW_MODES.LIST);
          resetToOriginalResults(); // Reset when changing views
        }}
        icon={<MenuOutlined />}
        style={{
          backgroundColor: viewMode === VIEW_MODES.LIST ? "#e0e0e0" : "",
          height: 50,
          width: 50,
        }}
      />

      {searchTab == HOTELS || searchTab == DYNAMIC_PACKAGES ? (
        <Button
          onClick={fetchLikedHotels}
          icon={<HeartOutlined />}
          style={{
            backgroundColor:
              viewMode === VIEW_MODES.LIKED ? "#e0e0e0" : "unset",
            border: "0px",
            height: 50,
            width: 50,
          }}
        ></Button>
      ) : (
        ""
      )}

      {searchTab !== PACKAGES && searchTab !== ORGANIZED_TOURS && (
        <Button
          type={viewMode === VIEW_MODES.MAP ? "default" : "text"}
          onClick={() => {
            setViewMode(VIEW_MODES.MAP);
            resetToOriginalResults(); // Reset when changing views
          }}
          icon={<EnvironmentOutlined />}
          style={{
            backgroundColor: viewMode === VIEW_MODES.MAP ? "#e0e0e0" : "",
            height: 50,
            width: 50,
          }}
        />
      )}
    </Space.Compact>
  );

  // Filter button matching counter style
  const FilterButton = () => (
    <Button
      onClick={toggleFilters}
      className="tw-h-[50px] tw-flex tw-items-center tw-justify-center tw-my-2"
      icon={showFilters ? <MinusOutlined /> : <PlusOutlined />}
    >
      <span className="tw-ml-1">
        {showFilters ? t("filters.hidefilters") : t("filters.showfilters")}
      </span>
      <FilterOutlined className="tw-ml-2" />
    </Button>
  );

  return (
    <>
      <div className="tw-h-full tw-overflow-hidden tw-max-w-full tw-box-border tw-m-0 tw-w-full lg:tw-px-5 tw-px-2">
        <div className="tw-flex tw-flex-col lg:tw-flex-col tw-h-full  tw-box-border tw-m-0">
          {searchTab === DYNAMIC_PACKAGES &&
          linkedFlights &&
          searchResults &&
          searchResults.length > 0 ? (
            <div className="tw-mb-5">
              <FlightSelection isCard isSelectable isCompact />
            </div>
          ) : null}

          <div className="tw-flex tw-flex-col md:tw-flex-row tw-h-full">
            <div
              className={`searchResults-item searchResults-results tw-overflow-scroll tw-flex tw-flex-col tw-h-full tw-max-w-full ${
                (viewMode === VIEW_MODES.CARDS && !isMobile) ||
                (viewMode == VIEW_MODES.LIKED && !isMobile)
                  ? "cards"
                  : ""
              } ${viewMode === VIEW_MODES.MAP || (isMobile && isMapVisible) ? "map-view" : ""} 
         
          
            `}
              style={{
                height: "100%",
                margin: 0,
                padding: 0,
                overflow: "auto",
                scrollbarWidth: "none",
                scrollbarColor: "transparent transparent",
                backgroundColor: "#fff",
                borderRadius: "10px",
              }}
            >
              {(searchTab === HOTELS ||
                searchTab === DYNAMIC_PACKAGES ||
                searchTab === PACKAGES ||
                searchTab === ORGANIZED_TOURS) &&
                searchResultsFiltered?.length > 0 && (
                  <div className="md:tw-flex md:tw-flex-1 tw-w-full md:tw-h-full tw-h-full tw-box-border tw-m-0 tw-p-0 ">
                    {/* Desktop View */}
                    {!isMobile && (
                      <div className="tw-border tw-border-gray-200 tw-rounded-lg tw-bg-gray-50 tw-shadow-sm tw-px-3 tw-py-2 tw-w-full">
                        <Row gutter={16} align="middle" justify="space-between">
                          <Col>
                            <ViewModeButtons />
                          </Col>

                          <Col>
                            <Row>
                              {(searchTab === PACKAGES ||
                                searchTab === DYNAMIC_PACKAGES ||
                                searchTab === ORGANIZED_TOURS ||
                                searchTab === HOTELS) && (
                                <div className="tw-h-[50px] tw-flex tw-items-center md:tw-max-w-[430px] tw-justify-center md:tw-pt-1">
                                  {!searchTab === DYNAMIC_PACKAGES && <Timer />}
                                  {totalResults > 0 && <FilterButton />}
                                </div>
                              )}
                            </Row>
                          </Col>
                        </Row>
                      </div>
                    )}

                    {/* Mobile View */}
                    {isMobile && (
                      <div className="tw-border tw-border-gray-200 tw-rounded-lg tw-bg-gray-50 tw-px-2 tw-py-2 tw-h-fit tw-rounded-b-none  tw-wrap">
                        <Row className="tw-flex tw-items-center tw-justify-center tw-w-full">
                          {(searchTab === PACKAGES || searchTab === HOTELS) && (
                            <Timer />
                          )}
                        </Row>
                        <Row
                          gutter={[8, 8]}
                          className="tw-flex tw-full tw-flex-row tw-items-center tw-justify-between"
                        >
                          <MobileMapToggle
                            isMapVisible={isMapVisible}
                            toggleMapView={toggleMapView}
                            totalResults={totalResults}
                          />
                          <Button
                            onClick={fetchLikedHotels}
                            className="tw-w-fit tw-flex tw-items-center tw-justify-center tw-min-w-fit"
                            icon={<HeartOutlined />}
                          ></Button>

                          <Button
                            onClick={toggleFilters}
                            className="tw-w-fit tw-flex tw-items-center tw-justify-center tw-min-w-fit "
                            icon={<FilterOutlined />}
                          >
                            {showFilters
                              ? t("filters.hidefilters")
                              : t("filters.showfilters")}
                          </Button>
                        </Row>
                      </div>
                    )}
                  </div>
                )}

              {/* Render search results */}
              {renderResults()}
              {/* Range search results section */}
              {!rangeSearchResults ? null : (
                <div>
                  <div
                    style={{
                      height: 10,
                      width: "100%",
                      backgroundColor: "red",
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Hotel Popup */}
        {isMobile && showMobilePopup && selectedHotel && (
          <MobileHotelPopup
            hotel={selectedHotel}
            onClose={() => setShowMobilePopup(false)}
            onViewDetails={handleViewDetails}
          />
        )}

        {/* Filters Modal - Works for both desktop and mobile */}
        <Modal
          open={showFilters}
          onCancel={() => setShowFilters(false)}
          footer={null}
          width={isMobile ? "90%" : 600}
          style={{ top: 20 }}
          maskClosable={true}
          className="filters-modal"
        >
          <div className="tw-max-h-[calc(70vh-56px)] tw-overflow-y-auto tw-p-2">
            {renderFilters()}
          </div>
        </Modal>
      </div>
    </>
  );
};

export default SearchResults;
